/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCarouselItem } from "../../../model/types";
import type { TContentCardModel } from "./ContentCardProps";

/**
 * Adapter from the carousel feed model to the card's presentation model.
 *
 * Kept in a separate module so {@link ContentCard} itself never imports
 * `TCarouselItem` — screens with a different item type build a
 * {@link TContentCardModel} directly and skip this file entirely.
 *
 * The derivation rules deliberately mirror `ContentPreview`, which is the
 * existing source of truth for how a feed item's metadata line reads.
 */

/** Badge text for content that is airing now. */
const BADGE_LIVE: string = "LIVE";

/** Badge text for a first-run episode. */
const BADGE_NEW: string = "NEW";

/** Suffix for the formatted duration fragment. */
const DURATION_SUFFIX: string = " min";

/** Star glyph prefixed to a movie's star rating. */
const STAR_GLYPH: string = "★ ";

/** Separator between the season/episode label and the episode title. */
const EPISODE_TITLE_SEPARATOR: string = " - ";

/**
 * Builds the episode line: `"S1 E4 - CQB"`, degrading to whichever half is
 * available.
 *
 * Split out of {@link resolveSubtitle} to keep each branch shallow.
 *
 * @param item - The feed item, already known to be an episode.
 * @returns The episode line, or `undefined` when neither half is present.
 */
const resolveEpisodeSubtitle = (item: TCarouselItem): string | undefined => {
  const hasNumbering: boolean =
    item.seasonNumber !== undefined && item.episodeNumber !== undefined;
  const episodeLabel: string = hasNumbering
    ? "S" + String(item.seasonNumber) + " E" + String(item.episodeNumber)
    : "";

  if (item.episodeTitle !== undefined && item.episodeTitle !== "") {
    return episodeLabel === ""
      ? item.episodeTitle
      : episodeLabel + EPISODE_TITLE_SEPARATOR + item.episodeTitle;
  }

  return episodeLabel === "" ? undefined : episodeLabel;
};

/**
 * Builds the episode / event line shown under the title.
 *
 * @param item - The feed item.
 * @returns The subtitle, or `undefined` when the item has no secondary line.
 */
const resolveSubtitle = (item: TCarouselItem): string | undefined => {
  if (item.contentType === "episode") {
    return resolveEpisodeSubtitle(item);
  }

  const isEvent: boolean =
    item.contentType === "sports" || item.contentType === "gameEvent";
  if (isEvent && item.gameTitle !== undefined && item.gameTitle !== "") {
    return item.gameTitle;
  }

  return undefined;
};

/**
 * Builds the metadata fragments joined by the card's middot separator, in the
 * same order `ContentPreview` renders them.
 *
 * @param item - The feed item.
 * @returns Fragments with visible content only — never `undefined` entries.
 */
const resolveMetadata = (item: TCarouselItem): string[] => {
  const isMovie: boolean = item.contentType === "movie";
  const fragments: string[] = [];

  if (item.ratingBadge !== undefined && item.ratingBadge !== "") {
    fragments.push(item.ratingBadge);
  }
  if (item.duration > 0) {
    fragments.push(String(Math.floor(item.duration / 60)) + DURATION_SUFFIX);
  }
  if (isMovie && item.movieYear !== undefined && item.movieYear > 0) {
    fragments.push(String(item.movieYear));
  }
  if (isMovie && item.starRating !== undefined && item.starRating > 0) {
    fragments.push(STAR_GLYPH + item.starRating.toFixed(1));
  }
  if (item.categoryLabel !== undefined && item.categoryLabel !== "") {
    fragments.push(item.categoryLabel);
  }
  if (item.providerName !== undefined && item.providerName !== "") {
    fragments.push(item.providerName);
  }

  return fragments;
};

/**
 * Resolves the badge overlaid on the thumbnail. LIVE outranks NEW, matching the
 * badge priority order in `Tile`.
 *
 * @param item - The feed item.
 * @returns The badge text, or `undefined` when no badge applies.
 */
const resolveBadgeText = (item: TCarouselItem): string | undefined => {
  if (item.isLive) {
    return BADGE_LIVE;
  }
  if (item.isNew === true) {
    return BADGE_NEW;
  }
  return undefined;
};

/**
 * Maps a carousel feed item onto the presentation-ready card model.
 *
 * Pure and framework-free, so screens can call it inside a `useMemo` or a
 * Redux selector without pulling React in.
 *
 * @param item - The feed item to adapt.
 * @returns The card model to hand to {@link ContentCard}.
 */
export const toContentCardModel = (item: TCarouselItem): TContentCardModel => {
  return {
    id: item.id,
    title: item.title,
    subtitle: resolveSubtitle(item),
    metadata: resolveMetadata(item),
    description: item.description === "" ? undefined : item.description,
    imageUrl: item.imageUrl === "" ? undefined : item.imageUrl,
    progressPercent: item.progressPercent,
    badgeText: resolveBadgeText(item)
  };
};
